import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useProfileTransferRequest from '../../../../../hooks/api/useProfileTransferRequest';
import { getIntl } from '../../../../../utils/intl';
import getName from '../../../../../utils/getName';
import useProfilePendingTransferRequest from '../../../../../hooks/api/useProfilePendingTransferRequest';
import { CenteredFormRow, Form } from '../../../../v2BaseComponents/Form';
import { FormStack } from '../../../../v2BaseComponents/Stack';
import { Field } from 'formik';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { consentCheck } from '../../../../../utils/validators';
import LabelValueDisplay from '../../../../v2BaseComponents/LabelValueDisplay';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('profile-ownership-transfer-request-decision', {
    content:
        "{fromUserFullName} has requested you to take ownership of {profileFullName}'s profile.  You can accept or reject this request.",
    ownerStillWantsAccess: 'The current owner has requested to maintain access to this profile.',
    ownerDoesNotWantAccess: 'The current owner has requested not to maintain access to this profile.',
    acceptRequest: 'Accept',
    rejectRequest: 'Reject',
    consent: "I accept the responsibility of administering {profileFullNames}'s profile.",
    keepAccess: 'Keep {fromUserFullName} as a collaborator on this profile.  {accessRequestMessage}',
    message: 'Message from current organizer:',
});

export type TransferRequestResponseScreenOwnProps = {};

export type TransferRequestResponseScreenProps = TransferRequestResponseScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'transfer-request-response'>;
};

type FormValue = {
    keepAccess: boolean;
    consentAccepted: boolean;
};

const TransferRequestResponseScreen: React.FC<TransferRequestResponseScreenProps> = (props) => {
    const { isLoading, profilePendingTransferRequest } = useProfilePendingTransferRequest({
        medicalProfileId: props.route.params.transferProfileId,
        transferRequestId: props.route.params.transferRequestId,
    });
    const navigation = useProfilesTabNavigation();
    const { respondToTransferRequest, isRespondingToTransferRequest } = useProfileTransferRequest({
        onRespondToTransferRequest: () => {
            navigation.navigate('root');
        },
    });

    const formValues = useMemo<FormValue>(
        () => ({
            keepAccess: !!profilePendingTransferRequest?.previousOwnerWantsToMaintainAccess,
            consentAccepted: false,
        }),
        [profilePendingTransferRequest?.previousOwnerWantsToMaintainAccess],
    );

    const fromUserFullName = profilePendingTransferRequest ? getName(profilePendingTransferRequest.fromUser) : '';
    const profileFullName = profilePendingTransferRequest?.profile
        ? getName(profilePendingTransferRequest.profile)
        : '';

    return (
        <AnimateLoadedContent isLoading={isLoading || !profilePendingTransferRequest || isRespondingToTransferRequest}>
            {() => (
                <Form
                    actionContainerSubmit
                    initialValues={formValues}
                    onSubmit={(values) =>
                        respondToTransferRequest({
                            profileId: props.route.params.transferProfileId,
                            decision: 'accept',
                            previousOwnerMaintainsAccess: values.keepAccess,
                        })
                    }
                    onCancel={() => {
                        respondToTransferRequest({
                            profileId: props.route.params.transferProfileId,
                            decision: 'reject',
                            previousOwnerMaintainsAccess: false,
                        });
                    }}
                    saveText={intl('acceptRequest')}
                    cancelButtonVariant="secondary"
                    cancelText={intl('rejectRequest')}
                >
                    {() => (
                        <FormStack>
                            <CenteredFormRow>
                                <Text variant="body-1">
                                    {intl('content', {
                                        fromUserFullName,
                                        profileFullName,
                                    })}
                                </Text>
                            </CenteredFormRow>
                            <CenteredFormRow>
                                {profilePendingTransferRequest?.message ? (
                                    <LabelValueDisplay
                                        items={[
                                            { label: intl('message'), value: profilePendingTransferRequest.message },
                                        ]}
                                    />
                                ) : null}
                            </CenteredFormRow>
                            <CenteredFormRow>
                                <Field
                                    name="keepAccess"
                                    component={FormikInputs.Checkbox}
                                    title={intl('keepAccess', {
                                        fromUserFullName,
                                        accessRequestMessage: intl(
                                            profilePendingTransferRequest?.previousOwnerWantsToMaintainAccess
                                                ? 'ownerStillWantsAccess'
                                                : 'ownerDoesNotWantAccess',
                                        ),
                                    })}
                                    testID="keep-access-checkbox"
                                />
                            </CenteredFormRow>
                            <CenteredFormRow>
                                <Field
                                    name="consentAccepted"
                                    component={FormikInputs.Checkbox}
                                    title={intl('consent', {
                                        profileFullNames: profileFullName,
                                    })}
                                    testID="consent-checkbox"
                                    validate={consentCheck}
                                />
                            </CenteredFormRow>
                        </FormStack>
                    )}
                </Form>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(TransferRequestResponseScreen);
